/**
 * Clerk Operations Centre — data-access layer.
 * Each function corresponds to one section of the Operations Centre page.
 * Where existing API modules handle the fetch, we delegate to them and
 * apply the clerk-specific filter locally.  Only fetchRequiringAttention
 * calls a net-new endpoint.
 */

import { api } from './api';
import { listAwaiting, type Allocation } from './allocationsApi';
import { fetchHearings } from './hearingsApi';
import type { Hearing } from './hearingsStore';
import { fetchTasks, type Task } from './tasksApi';
import { fetchFeeNotes, type FeeNoteListRow } from './feeNotesApi';
import { fetchAgedDebt, type AgedDebtRow } from './feeNotesApi';
import { fetchThreads, type MessageThread } from './messagingApi';

// ─── Types ───────────────────────────────────────────────────────────────────

export type { Allocation, Hearing, Task, FeeNoteListRow, AgedDebtRow, MessageThread };

/** Minimal matter shape for Operations Centre sections. */
export interface OpsNewMatter {
  id: string;
  caseNumber: string;
  caseName: string;
  status: string;
  createdAt: string;
  practiceArea: { id: string; name: string } | null;
  leadBarrister: { id: string; user: { id: string; firstName: string; lastName: string } } | null;
  clerk: { id: string; firstName: string; lastName: string } | null;
}

export type AttentionReason = 'missing_outcome' | 'overdue_tasks' | 'stale';

export interface AttentionItem {
  matterId: string;
  caseNumber: string;
  caseName: string;
  status: string;
  reasons: AttentionReason[];
  /** ISO date of the most relevant event */
  referenceDate: string | null;
  /** Human-readable reason summary */
  detail: string;
}

// ─── Section 1: New Matters (last 48 h, intake status) ───────────────────────

export async function fetchNewMatters(): Promise<OpsNewMatter[]> {
  const res = await api.get<{ data: { matters: OpsNewMatter[] } }>('/matters', {
    params: { status: 'intake', limit: 50, sortBy: 'createdAt', sortOrder: 'desc' },
  });
  const matters: OpsNewMatter[] = res.data?.data?.matters ?? [];
  const cutoffMs = Date.now() - 48 * 60 * 60 * 1000;
  return matters.filter((m) => new Date(m.createdAt).getTime() >= cutoffMs);
}

// ─── Section 2: Awaiting Barrister Response ───────────────────────────────────

export async function fetchPendingAllocations(): Promise<Allocation[]> {
  return listAwaiting();
}

// ─── Section 3: Hearings — next 7 days ────────────────────────────────────────

export async function fetchUpcomingHearings(): Promise<Hearing[]> {
  const all = await fetchHearings();
  const now = new Date();
  const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return all.filter((h) => {
    const d = new Date(h.date);
    return (
      d >= now &&
      d <= in7Days &&
      (h.status === 'scheduled' || h.status === 'confirmed')
    );
  });
}

// ─── Section 4: Tasks Due Today (assigned to the current clerk) ──────────────

const ACTIVE_TASK_STATUSES = new Set([
  'pending',
  'in_progress',
  'awaiting_barrister',
  'awaiting_clerk',
  'hearing_followup',
  'filing_pending',
  'blocked',
]);

export async function fetchTasksDueToday(assignedToId: string): Promise<Task[]> {
  const { tasks } = await fetchTasks({
    assignedToId,
    sortBy: 'dueDate',
    sortOrder: 'asc',
    limit: 100,
  });
  const todayStr = new Date().toISOString().slice(0, 10);
  return tasks.filter(
    (t) => t.dueDate?.slice(0, 10) === todayStr && ACTIVE_TASK_STATUSES.has(t.status),
  );
}

// ─── Section 5: Outstanding Fee Notes ────────────────────────────────────────

const OUTSTANDING_STATUSES = new Set(['sent', 'overdue', 'partially_paid']);

export async function fetchOutstandingFeeNotes(): Promise<FeeNoteListRow[]> {
  const { feeNotes } = await fetchFeeNotes({ limit: 100 });
  return feeNotes
    .filter((fn) => OUTSTANDING_STATUSES.has(fn.status))
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
}

// ─── Section 6: Overdue Payments (aged-debt breakdown) ────────────────────────

export interface AgedDebtSummary {
  rows: AgedDebtRow[];
  /** Rows that have at least one overdue bucket */
  overdueRows: AgedDebtRow[];
  totals: { d30: number; d60: number; d90: number; total: number };
}

export async function fetchOverduePayments(): Promise<AgedDebtSummary> {
  const rows = await fetchAgedDebt();
  const overdueRows = rows.filter((r) => r.d30 > 0 || r.d60 > 0 || r.d90 > 0);
  const totals = overdueRows.reduce(
    (acc, r) => ({
      d30: acc.d30 + r.d30,
      d60: acc.d60 + r.d60,
      d90: acc.d90 + r.d90,
      total: acc.total + r.d30 + r.d60 + r.d90,
    }),
    { d30: 0, d60: 0, d90: 0, total: 0 },
  );
  return { rows, overdueRows, totals };
}

// ─── Section 7: Matters Requiring Attention ──────────────────────────────────

export async function fetchRequiringAttention(): Promise<AttentionItem[]> {
  const res = await api.get<{ data: AttentionItem[] }>('/matters/requiring-attention');
  return res.data.data ?? [];
}

// ─── Section 8: Unread Communications ────────────────────────────────────────

export async function fetchUnreadThreads(): Promise<MessageThread[]> {
  const threads = await fetchThreads({ limit: 50 });
  return threads.filter((t) => (t.unreadCount ?? 0) > 0);
}
